import { useEffect, useState, useMemo, useRef, type CSSProperties, type FormEvent } from "react";
import {
  listEmailTemplates,
  saveEmailTemplate,
  deleteEmailTemplate,
  type EmailTemplate,
} from "../../lib/api";
import PublishingStudioTabs from "../../components/admin/PublishingStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";

const CATEGORIES = [
  { id: "all", label: "All Templates" },
  { id: "campaign", label: "Campaign", color: "#F9AF36" },
  { id: "event", label: "Event & RSVP", color: "#4A8FD9" },
  { id: "lifecycle", label: "Lifecycle", color: "#27ae60" },
  { id: "membership", label: "C33 Membership", color: "#9B59B6" },
  { id: "system", label: "System & Ops", color: "var(--muted)" },
];

const MERGE_VARS = [
  { tag: "{{first_name}}", label: "First Name" },
  { tag: "{{event_title}}", label: "Event Title" },
  { tag: "{{event_date}}", label: "Event Date" },
  { tag: "{{event_time}}", label: "Event Time" },
  { tag: "{{location}}", label: "Location" },
  { tag: "{{guests}}", label: "Guest Count" },
  { tag: "{{ticket_type}}", label: "Ticket Tier" },
  { tag: "{{description}}", label: "Description" },
  { tag: "{{order_id}}", label: "Order ID" },
];

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export default function AdminTemplates() {
  // Phones only (L121). slugify joins words with "_", so a key made from a long name has no
  // break point and ran the card off a 375 screen (560px page).
  const isPhone = useIsMobile(768);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Editor State
  const [editingTemplate, setEditingTemplate] = useState<Partial<EmailTemplate> | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"html" | "preview" | "text">("html");
  const [saving, setSaving] = useState(false);

  // Textarea Ref for cursor insertion
  const htmlInputRef = useRef<HTMLTextAreaElement | null>(null);

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes editor
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isEditorOpen) setIsEditorOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isEditorOpen]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await listEmailTemplates();
      setTemplates(data || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load templates", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const filteredTemplates = useMemo(() => {
    let list = templates;
    if (selectedCategory !== "all") {
      list = list.filter((t) => (t.category || "system").toLowerCase() === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.template_key.toLowerCase().includes(q) ||
          t.subject.toLowerCase().includes(q)
      );
    }
    return list;
  }, [templates, selectedCategory, searchQuery]);

  const handleNewTemplate = () => {
    setEditingTemplate({
      name: "",
      slug: "",
      template_key: "",
      subject: "Your Sentinel Update: {{event_title}}",
      category: "event",
      html_body: `<div style="font-family:Georgia,serif;color:#1B1610;padding:24px;">\n  <h1 style="color:#F9AF36;">Hi {{first_name}},</h1>\n  <p>You are confirmed for <strong>{{event_title}}</strong> on {{event_date}} at {{event_time}}.</p>\n  <p>Location: {{location}}</p>\n</div>`,
      text_body: "Hi {{first_name}}, You are confirmed for {{event_title}} on {{event_date}} at {{event_time}}.",
      is_active: true,
    });
    setEditorMode("html");
    setIsEditorOpen(true);
  };

  const handleEditTemplate = (t: EmailTemplate) => {
    setEditingTemplate({ ...t });
    setEditorMode("html");
    setIsEditorOpen(true);
  };

  const handleInsertVar = (tag: string) => {
    if (!editingTemplate) return;
    const textarea = htmlInputRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const oldVal = editingTemplate.html_body || "";
      const newVal = oldVal.substring(0, start) + tag + oldVal.substring(end);
      setEditingTemplate({ ...editingTemplate, html_body: newVal });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + tag.length, start + tag.length);
      }, 0);
    } else {
      setEditingTemplate({
        ...editingTemplate,
        html_body: (editingTemplate.html_body || "") + " " + tag,
      });
    }
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingTemplate || !editingTemplate.name || !editingTemplate.template_key) return;
    setSaving(true);
    try {
      const payload: Partial<EmailTemplate> = {
        ...editingTemplate,
        slug: editingTemplate.slug || slugify(editingTemplate.name),
      };
      await saveEmailTemplate(payload);
      showToast(`✓ Template "${payload.name}" saved!`);
      setIsEditorOpen(false);
      await loadTemplates();
    } catch (err: any) {
      showToast(err.message || "Failed to save template", "err");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete template "${name}"?`)) return;
    try {
      await deleteEmailTemplate(id);
      showToast(`✓ Deleted "${name}"`);
      setIsEditorOpen(false);
      await loadTemplates();
    } catch (err: any) {
      showToast(err.message || "Failed to delete template", "err");
    }
  };

  // Preview renderer with mock variable replacement
  const renderedPreviewHtml = useMemo(() => {
    if (!editingTemplate || !editingTemplate.html_body) return "";
    let html = editingTemplate.html_body;
    const mockVars: Record<string, string> = {
      "{{first_name}}": "Travis",
      "{{event_title}}": "S3 Vol. 09: Woodford Reserve Master Class",
      "{{event_date}}": "Thursday, September 24, 2026",
      "{{event_time}}": "6:30 PM – 9:00 PM",
      "{{location}}": "Sentinel's Reserve Whiskey Library",
      "{{guests}}": "2",
      "{{ticket_type}}": "C33 Member VIP",
      "{{description}}": "Intimate single barrel tasting with Master Distiller.",
      "{{order_id}}": "SQ-98421",
    };
    for (const [v, val] of Object.entries(mockVars)) {
      html = html.split(v).join(val);
    }
    return html;
  }, [editingTemplate]);

  return (
    <div style={styles.container}>
      <PublishingStudioTabs activeTab="templates" badgeCounts={{ templates: templates.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Notification &amp; Email Templates</h1>
          <p style={styles.subtitle}>
            Manage transactional emails, RSVP confirmations, C33 membership perks, and AI voice prompts.
          </p>
        </div>
        <button type="button" style={styles.btnPrimary} onClick={handleNewTemplate}>
          + New Template
        </button>
      </div>

      {/* Category Chips & Search */}
      <div style={styles.filterRow}>
        <div style={styles.categoryChips}>
          {CATEGORIES.map((cat) => {
            const count =
              cat.id === "all"
                ? templates.length
                : templates.filter((t) => (t.category || "system").toLowerCase() === cat.id).length;
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                style={{
                  ...styles.chip,
                  borderColor: isActive ? "var(--gold)" : "var(--border)",
                  background: isActive ? "rgba(249,175,54,0.15)" : "var(--surface)",
                  color: isActive ? "var(--gold)" : "var(--muted)",
                }}
                onClick={() => setSelectedCategory(cat.id)}
              >
                {cat.label} <span style={styles.chipCount}>{count}</span>
              </button>
            );
          })}
        </div>
        <input
          type="text"
          placeholder="Search templates by name, key, subject…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Templates List */}
      {loading ? (
        <div style={styles.loader}>Loading templates…</div>
      ) : filteredTemplates.length === 0 ? (
        <div style={styles.emptyState}>No templates found matching your filters.</div>
      ) : (
        <div style={styles.templateGrid}>
          {filteredTemplates.map((t) => {
            const cat = CATEGORIES.find((c) => c.id === (t.category || "system")) || CATEGORIES[5];
            return (
              <div
                key={t.id}
                style={isPhone ? { ...styles.templateCard, minWidth: 0 } : styles.templateCard}
                onClick={() => handleEditTemplate(t)}
              >
                <div style={styles.cardHeader}>
                  <div>
                    <span
                      style={{
                        ...styles.catBadge,
                        background: cat.color ? `${cat.color}22` : "rgba(255,255,255,0.08)",
                        color: cat.color || "var(--text)",
                      }}
                    >
                      {cat.label}
                    </span>
                    <h3 style={styles.cardTitle}>{t.name}</h3>
                  </div>
                  <span
                    style={{
                      ...styles.statusBadge,
                      background: t.is_active ? "rgba(39,174,96,0.15)" : "rgba(138,122,102,0.15)",
                      color: t.is_active ? "#27ae60" : "var(--muted)",
                    }}
                  >
                    {t.is_active ? "ACTIVE" : "INACTIVE"}
                  </span>
                </div>
                <div style={styles.cardSubject}>
                  <strong>Subject:</strong> {t.subject}
                </div>
                <div style={isPhone ? { ...styles.cardKey, overflowWrap: "break-word" } : styles.cardKey}>
                  Key: <code>{t.template_key}</code>
                </div>
                <div style={styles.cardFooter}>
                  <span>Updated {new Date(t.updated_at).toLocaleDateString()}</span>
                  <span style={{ color: "var(--gold)", fontWeight: 700 }}>Edit Template →</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor Modal */}
      {isEditorOpen && editingTemplate && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingTemplate.id ? `Edit: ${editingTemplate.name}` : "New Email Template"}
              </h2>
              <button
                type="button"
                style={styles.btnIcon}
                onClick={() => setIsEditorOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSave} style={styles.modalForm}>
              <div className="station-form-grid" style={styles.formGrid}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Template Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Event RSVP Confirmation"
                    value={editingTemplate.name || ""}
                    onChange={(e) => {
                      const name = e.target.value;
                      setEditingTemplate({
                        ...editingTemplate,
                        name,
                        slug: editingTemplate.slug || slugify(name),
                        template_key: editingTemplate.template_key || slugify(name),
                      });
                    }}
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Template Key (System ID) *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. rsvp_confirmation"
                    value={editingTemplate.template_key || ""}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, template_key: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Category</label>
                  <select
                    value={editingTemplate.category || "event"}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, category: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="event">Event &amp; RSVP</option>
                    <option value="campaign">Campaign &amp; Broadcast</option>
                    <option value="lifecycle">Lifecycle &amp; Onboarding</option>
                    <option value="membership">C33 Membership</option>
                    <option value="system">System &amp; Voice</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Status</label>
                  <select
                    value={editingTemplate.is_active ? "true" : "false"}
                    onChange={(e) =>
                      setEditingTemplate({
                        ...editingTemplate,
                        is_active: e.target.value === "true",
                      })
                    }
                    style={styles.select}
                  >
                    <option value="true">Active (Serving live)</option>
                    <option value="false">Inactive (Draft)</option>
                  </select>
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Email Subject Line *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. You're confirmed for {{event_title}}!"
                    value={editingTemplate.subject || ""}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, subject: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Merge Variable Tags Bar */}
              <div style={styles.varBar}>
                <span style={styles.varLabel}>Insert Variable:</span>
                {MERGE_VARS.map((v) => (
                  <button
                    key={v.tag}
                    type="button"
                    style={styles.varTag}
                    onClick={() => handleInsertVar(v.tag)}
                    title={`Click to insert ${v.label}`}
                  >
                    {v.tag}
                  </button>
                ))}
              </div>

              {/* Mode Toggle: HTML / Plain Text / Live Preview */}
              <div style={styles.modeToggleRow}>
                <button
                  type="button"
                  style={editorMode === "html" ? styles.modeBtnActive : styles.modeBtn}
                  onClick={() => setEditorMode("html")}
                >
                  HTML Template
                </button>
                <button
                  type="button"
                  style={editorMode === "text" ? styles.modeBtnActive : styles.modeBtn}
                  onClick={() => setEditorMode("text")}
                >
                  Plain Text Fallback
                </button>
                <button
                  type="button"
                  style={editorMode === "preview" ? styles.modeBtnActive : styles.modeBtn}
                  onClick={() => setEditorMode("preview")}
                >
                  Live Visual Preview
                </button>
              </div>

              {/* Editor Body */}
              {editorMode === "html" && (
                <div style={styles.editorWrap}>
                  <textarea
                    ref={htmlInputRef}
                    rows={12}
                    placeholder="Enter email HTML markup..."
                    value={editingTemplate.html_body || ""}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, html_body: e.target.value })
                    }
                    style={styles.codeTextarea}
                  />
                </div>
              )}

              {editorMode === "text" && (
                <div style={styles.editorWrap}>
                  <textarea
                    rows={12}
                    placeholder="Enter plain text fallback content..."
                    value={editingTemplate.text_body || ""}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, text_body: e.target.value })
                    }
                    style={styles.codeTextarea}
                  />
                </div>
              )}

              {editorMode === "preview" && (
                <div style={styles.previewContainer}>
                  <iframe
                    title="Template HTML Preview"
                    srcDoc={renderedPreviewHtml}
                    sandbox=""
                    style={styles.previewIframe}
                  />
                </div>
              )}

              {/* Footer Actions */}
              <div style={styles.modalActions}>
                {editingTemplate.id && (
                  <button
                    type="button"
                    style={styles.btnDanger}
                    onClick={() =>
                      handleDelete(editingTemplate.id!, editingTemplate.name || "Template")
                    }
                  >
                    Delete Template
                  </button>
                )}
                <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
                  <button
                    type="button"
                    style={styles.btnSecondary}
                    onClick={() => setIsEditorOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" style={styles.btnPrimary} disabled={saving}>
                    {saving ? "Saving…" : "Save Template"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  filterRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 },
  categoryChips: { display: "flex", gap: 6, flexWrap: "wrap" },
  chip: { border: "1px solid", borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  chipCount: { background: "rgba(0,0,0,0.2)", borderRadius: 10, padding: "1px 6px", fontSize: 10 },
  searchInput: { minWidth: 260, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  templateGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 },
  templateCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, cursor: "pointer", transition: "border-color 0.15s ease", display: "flex", flexDirection: "column", gap: 8 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  catBadge: { fontSize: 10, fontWeight: 800, textTransform: "uppercase", padding: "2px 8px", borderRadius: 4, letterSpacing: 0.5 },
  statusBadge: { fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4 },
  cardTitle: { fontSize: 16, fontWeight: 700, margin: "6px 0 0", color: "var(--text)" },
  cardSubject: { fontSize: 12, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  cardKey: { fontSize: 11, color: "var(--muted)", fontFamily: "monospace" },
  cardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 4, fontSize: 11, color: "var(--muted)" },
  // minWidth "min-content": global.css gives phone buttons min-width 44px, which replaces a flex
  // item's content-size floor, so this wrapping label shrank past its own text (4d verifier).
  btnPrimary: { background: "var(--gold)", color: "#111", border: 0, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", minWidth: "min-content" },
  btnSecondary: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnDanger: { background: "transparent", color: "#E05040", border: "1px solid rgba(224,80,64,0.4)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnIcon: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 22, cursor: "pointer" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modalContent: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 840, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  modalForm: { padding: 20, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", letterSpacing: 0.5 },
  input: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  select: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  varBar: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px" },
  varLabel: { fontSize: 11, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", marginRight: 4 },
  varTag: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--gold)", padding: "2px 8px", fontSize: 11, fontFamily: "monospace", cursor: "pointer" },
  modeToggleRow: { display: "flex", gap: 8, borderBottom: "1px solid var(--border)", paddingBottom: 6 },
  modeBtn: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 6, cursor: "pointer" },
  modeBtnActive: { background: "rgba(249,175,54,0.15)", border: 0, color: "var(--gold)", fontSize: 12, fontWeight: 700, padding: "6px 12px", borderRadius: 6, cursor: "pointer" },
  editorWrap: { width: "100%" },
  codeTextarea: { width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: 12, fontFamily: "monospace", fontSize: 13, resize: "vertical" },
  previewContainer: { background: "#FFFFFF", borderRadius: 8, padding: 8, minHeight: 360, border: "1px solid var(--border)" },
  previewIframe: { width: "100%", height: 360, border: 0, background: "#FFF", borderRadius: 4, display: "block" },
  modalActions: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 16 },
};
